/**
 * Bootstrap reports providers. Combines everything needed to initialize the reports module.
 */
import { Signal } from '@angular/core';

import { Observable } from 'rxjs';

import { createProviderToken, TypedProvider } from '@core/common-legacy';
import { HttpApi } from '@core/http-types';
import {
  ReportsAppKind,
  ReportDashboardMetricOptions,
  ReportsTranslations,
} from '@core/reports-types';

import { provideTimezone } from './dashboard-grid';

const { inject: injectReportsTranslations, provide: provideReportsTranslations } =
  createProviderToken<ReportsTranslations>('ReportsTranslations')();
const { inject: injectReportsMetrics, provide: provideReportsMetrics } =
  createProviderToken<Observable<ReportDashboardMetricOptions[]>>('ReportsMetrics')();
const { inject: injectReportsAppKind, provide: provideReportsAppKind } =
  createProviderToken<ReportsAppKind>('ReportsAppKind')();
const { inject: injectReportsCompanyId, provide: provideReportsCompanyId } = createProviderToken<
  string | number
>('ReportsCompanyId')();
// Api service used by all core reports data features
const { inject: injectReportsHttpApiService, provide: provideReportsHttpApiService } =
  createProviderToken<HttpApi>('ReportsApiService')();
const { inject: injectReportsGlobalConfig, provide: provideReportsGlobalConfig } =
  createProviderToken<{ fullscreenAvailable: boolean }>('ReportsGlobalConfig')();

export interface ReportsBootstrapProviders {
  appKind: ReportsAppKind;
  httpApiService: TypedProvider<HttpApi>;
  translations: TypedProvider<ReportsTranslations>;
  metrics: TypedProvider<Observable<ReportDashboardMetricOptions[]>>;
  companyId: TypedProvider<string | number>;
  // TODO shared-analytics remove at some point fullscreen option. Config can likely stay
  config: { fullscreenAvailable: boolean };
  timezone: TypedProvider<Signal<string | null>>;
}

export const provideReports = ({
  appKind,
  httpApiService,
  metrics,
  translations,
  companyId,
  config,
  timezone,
}: ReportsBootstrapProviders) => [
  provideReportsAppKind({
    useValue: appKind,
  }),
  provideReportsHttpApiService(httpApiService),
  provideReportsMetrics(metrics),
  provideReportsTranslations(translations),
  ...(companyId ? [provideReportsCompanyId(companyId)] : []),
  provideReportsGlobalConfig({ useValue: config }),
  provideTimezone(timezone),
];

export {
  injectReportsAppKind,
  injectReportsHttpApiService,
  injectReportsTranslations,
  injectReportsMetrics,
  injectReportsCompanyId,
  injectReportsGlobalConfig,
  provideReportsGlobalConfig,
  // exported for testing purposes
  provideReportsTranslations,
};
